
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Trophy, Users, Calendar, Medal } from 'lucide-react';

interface LeaderboardSettings {
  contestPeriod: string;
  startDate: Date;
  endDate: Date;
  visibilityType: string;
  topPositionsCount: number;
  firstPlacePrize: string;
  secondPlacePrize: string;
  thirdPlacePrize: string;
  prizeType: string;
  minimumReferrals: number;
  showRealTimeUpdates: boolean;
  allowTies: boolean;
  displayUserProfiles: boolean;
}

interface LeaderboardPreviewProps {
  settings: LeaderboardSettings;
}

const LeaderboardPreview: React.FC<LeaderboardPreviewProps> = ({ settings }) => {
  // Mock data for leaderboard preview
  const leaderboardEntries = [
    { id: 1, name: 'Amanda Smith', referrals: 32, position: 1 },
    { id: 2, name: 'John Roberts', referrals: 27, position: 2 },
    { id: 3, name: 'Lisa Johnson', referrals: 24, position: 3 },
    { id: 4, name: 'Michael Chen', referrals: 18, position: 4 },
    { id: 5, name: 'David Wilson', referrals: 15, position: 5 },
  ];
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const getPositionBadgeColor = (position: number) => {
    switch (position) {
      case 1:
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 2:
        return 'bg-gray-400 hover:bg-gray-500';
      case 3:
        return 'bg-amber-700 hover:bg-amber-800';
      default:
        return 'bg-slate-200 text-slate-700 hover:bg-slate-300';
    }
  };

  const getPrizeForPosition = (position: number) => {
    switch (position) {
      case 1:
        return settings.firstPlacePrize;
      case 2:
        return settings.secondPlacePrize;
      case 3:
        return settings.thirdPlacePrize;
      default:
        return '';
    }
  };

  const formatPrize = (prize: string, type: string) => {
    if (!prize) return '';
    
    switch (type) {
      case 'gift-card':
      case 'store-credit':
      case 'cash':
        return `$${prize}`;
      case 'discount':
        return `${prize}% off`;
      default:
        return prize;
    }
  };

  const displayPeriod = () => {
    switch (settings.contestPeriod) {
      case 'weekly':
        return 'Weekly Contest';
      case 'monthly':
        return 'Monthly Contest';
      case 'quarterly':
        return 'Quarterly Contest';
      case 'custom':
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
        const startFormatted = new Date(settings.startDate).toLocaleDateString(undefined, options);
        const endFormatted = new Date(settings.endDate).toLocaleDateString(undefined, options);
        return `${startFormatted} to ${endFormatted}`;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center text-xl">
            <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
            Referral Leaderboard
          </CardTitle>
          <Badge variant="outline" className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            {displayPeriod()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-gray-500 mb-4 flex items-center">
          <Users className="h-4 w-4 mr-1" />
          <span>{leaderboardEntries.length} participants</span>
          {settings.minimumReferrals > 1 && (
            <span className="ml-2">· Min. {settings.minimumReferrals} referrals to qualify</span>
          )}
        </div>
        
        <div className="space-y-3">
          {leaderboardEntries.map((entry) => (
            <div key={entry.id} className="flex items-center p-2 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="w-8 text-center">
                {entry.position <= 3 ? (
                  <Badge className={`${getPositionBadgeColor(entry.position)} px-2 py-1`}>
                    {entry.position}
                  </Badge>
                ) : (
                  <span className="text-gray-400 font-medium">{entry.position}</span>
                )}
              </div>
              
              <div className="ml-3 flex-1 flex items-center">
                {settings.displayUserProfiles && (
                  <Avatar className="h-8 w-8 mr-2">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(entry.name)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <span className="font-medium">{entry.name}</span>
              </div>
              
              <div className="flex flex-col items-end">
                <div className="text-sm font-semibold">
                  {entry.referrals} referrals
                </div>
                {entry.position <= 3 && (
                  <div className="text-xs text-gray-500 flex items-center">
                    <Medal className="h-3 w-3 mr-1" />
                    {formatPrize(getPrizeForPosition(entry.position), settings.prizeType)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {settings.showRealTimeUpdates && (
          <div className="mt-4 text-xs text-center text-gray-400">
            Leaderboard updates in real-time as referrals are completed
          </div>
        )}
        
        <div className="mt-6 text-center">
          <div className="text-sm font-medium">Prizes</div>
          <div className="flex justify-center gap-3 mt-2">
            <div className="text-center px-3 py-2 bg-yellow-50 rounded-md">
              <div className="text-xs text-gray-500">1st Place</div>
              <div className="font-semibold text-yellow-600">
                {formatPrize(settings.firstPlacePrize, settings.prizeType)}
              </div>
            </div>
            <div className="text-center px-3 py-2 bg-gray-50 rounded-md">
              <div className="text-xs text-gray-500">2nd Place</div>
              <div className="font-semibold text-gray-600">
                {formatPrize(settings.secondPlacePrize, settings.prizeType)}
              </div>
            </div>
            <div className="text-center px-3 py-2 bg-amber-50 rounded-md">
              <div className="text-xs text-gray-500">3rd Place</div>
              <div className="font-semibold text-amber-700">
                {formatPrize(settings.thirdPlacePrize, settings.prizeType)}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaderboardPreview;
